import { useState } from "react";
import { AccionesUsuarioCabecera, FilaVerComoMovil } from "@/components/admin/AccionesUsuarioCabecera";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  CircleDollarSign,
  Banknote,
  CalendarDays,
  ShieldCheck,
  FileCheck,
  Users,
  Settings2,
  Ticket,
  Wallet,
  Menu,
  LucideIcon,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { TabBarPlano } from "@/components/ui/tab-bar";
import { KpiCardUniforme } from "@/components/ui/kpi-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/data-states";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { supabase } from "@/integrations/supabase/client";
import { CondominioProvider, useCondominio } from "@/contexts/CondominioContext";
import {
  CondominioImpersonationProvider,
  useCondominioImpersonation,
} from "@/contexts/CondominioImpersonationContext";
import { CondominioImpersonationSelector } from "./CondominioImpersonationSelector";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { usePortalNavEstado } from "@/hooks/usePortalNav";
import { Skeleton } from "@/components/ui/skeleton";
import { EsqueletoPagina } from "@/components/ui/esqueleto-portal";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";

const CONDOMINIO_MENU_ID = 30;

const iconMap: Record<string, LucideIcon> = {
  "/admin/portal-condominio/dashboard":     LayoutDashboard,
  "/admin/portal-condominio/departamentos": Building2,
  "/admin/portal-condominio/cobranza":      CircleDollarSign,
  "/admin/portal-condominio/titularidad":   FileCheck,
  "/admin/portal-condominio/residentes":    Users,
  "/admin/portal-condominio/tickets":       Ticket,
  "/admin/portal-condominio/tesoreria":     Banknote,
  "/admin/portal-condominio/presupuesto":   Wallet,
  "/admin/portal-condominio/amenidades":    CalendarDays,
  "/admin/portal-condominio/auditoria":     ShieldCheck,
  "/admin/portal-condominio/configuracion": Settings2,
};

/**
 * Selector del condominio en el header, con el mismo diseño que "Ver como" (ui/selector-ver-como):
 * etiqueta chica arriba, el nombre abajo y una lista con búsqueda. Antes era un <select> nativo.
 */
function CondominioSelector({ className }: { className?: string }) {
  const { condominios, proyectoId, setProyectoId, isLoading } = useCondominio();
  const [open, setOpen] = useState(false);
  const actual = condominios.find((c) => c.id === proyectoId);
  const etiqueta = isLoading ? "Cargando…" : actual?.nombre ?? (condominios.length ? "Elige un condominio" : "Sin condominios");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`Condominio: ${etiqueta}`}
          disabled={isLoading || condominios.length === 0}
          className={cn("h-9 w-full min-w-0 justify-between gap-2 border-control-border bg-background px-2.5 sm:w-[200px]", className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Building2 className="size-3.5" strokeWidth={1.75} />
            </span>
            <span className="flex min-w-0 flex-col items-start gap-px">
              <span className="text-[10px] font-normal uppercase leading-none tracking-wide text-muted-foreground">Condominio</span>
              {/* leading-tight: con leading-none el truncate recortaba las letras con cola (la g de Margot). */}
              <span className="max-w-full truncate text-[13px] font-medium leading-tight">{etiqueta}</span>
            </span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(88vw,260px)] p-0" align="end">
        <Command>
          <CommandInput placeholder="Buscar condominio…" className="h-9 text-[13px]" />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>Sin condominios con ese nombre</CommandEmpty>
            <CommandGroup>
              {condominios.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.nombre}
                  className="py-1.5"
                  onSelect={() => { setProyectoId(c.id); setOpen(false); }}
                >
                  <Check className={cn("mr-2 size-3.5 shrink-0", c.id === proyectoId ? "opacity-100" : "opacity-0")} />
                  <span className="min-w-0 flex-1 truncate text-[13px]">{c.nombre}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const PortalCondominioLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { items: navFromDb, cargando: navCargando } = usePortalNavEstado(CONDOMINIO_MENU_ID, iconMap, LayoutDashboard);
  // Sin condominios asignados cada menú mostraría ceros: se dice una vez aquí.
  const { sinAcceso, isLoading: condominiosCargando } = useCondominio();
  const { isPathAllowed, isPathDisabled, isLoading: permisosCargando } = useAllowedMenus();
  const { impersonatedRolId, isImpersonating } = useCondominioImpersonation();

  // Al impersonar: paths que el ROL impersonado puede LEER (submenus_permisos),
  // para pintar el sidebar como lo vería ese usuario (sin bypass de super admin).
  // El rol lo valida CondominioImpersonationProvider contra `usuarios`; hasta tenerlo, esqueleto.
  // Las acciones de cada pantalla quedan en solo lectura (usePortalPagePermissions).
  const { data: impersonatedPaths, isPending: impersonadoPendiente } = useQuery({
    queryKey: ["condominio-impersonated-paths", impersonatedRolId],
    enabled: isImpersonating && impersonatedRolId != null,
    queryFn: async () => {
      const { data: leer } = await (supabase as any)
        .from("permisos").select("id").eq("nombre", "leer").maybeSingle();
      const leerId = (leer as any)?.id;
      const { data: perms } = await (supabase as any)
        .from("submenus_permisos")
        .select("submenu_id")
        .eq("rol_id", impersonatedRolId)
        .eq("activo", true)
        .eq("permiso_id", leerId);
      const ids = (perms ?? []).map((p: any) => p.submenu_id);
      const set = new Set<string>();
      if (ids.length) {
        const { data: subs } = await (supabase as any)
          .from("submenus").select("vista_front_end").in("id", ids);
        (subs ?? []).forEach((s: any) => { if (s.vista_front_end) set.add(s.vista_front_end); });
      }
      return set;
    },
  });

  // ¿El rol EFECTIVO puede ver este path? Impersonando → permisos del rol
  // impersonado; si no → permisos del rol real (incluye wildcard Super Admin).
  const isEffectivelyAllowed = (path: string) =>
    isImpersonating ? (impersonatedPaths?.has(path) ?? false) : isPathAllowed(path);

  const navItems = (() => {
    const items = navFromDb.slice();
    // Menús por rol: solo los permitidos para el rol efectivo, y ocultar los
    // apagados en BD (submenu activo=false o menú padre inactivo).
    return items.filter((i) => isEffectivelyAllowed(i.path) && !isPathDisabled(i.path));
  })();

  // Hasta tener nav, permisos y condominios el portal va en esqueleto: antes pintaba los menús
  // inyectados del front (Titularidad, Presupuesto) y un "sin acceso" que saltaban al llegar la BD.
  const cargandoPortal = navCargando || permisosCargando || condominiosCargando
    || (isImpersonating && (impersonatedRolId == null || impersonadoPendiente));

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");
  const currentSection = navItems.find((i) => isActive(i.path))?.label || "Condominio";
  const isSuperAdmin = profile?.rol_id === 1;
  const canImpersonate = profile?.puede_impersonar === true;
  const { canReturnToAdmin } = useCanReturnToAdmin();

  // Perfil: con "Ver como", la persona impersonada (nombre y rol suyos), no el admin.
  const perfil = usePerfilVista({ rol: profile?.rol_nombre ?? "Condominio" });
  const userName = perfil.nombre.split(/\s+/).slice(0, 2).join(" ");
  const userRole = perfil.rol || "Condominio";
  const initials = perfil.iniciales;

  const go = (path: string) => { navigate(path); setMobileOpen(false); };

  const sidebar = (
    <PortalSidebar
      portal="Portal Condominio"
      miCuenta
      cargando={cargandoPortal}
      groups={[{
        items: navItems.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => go(item.path),
        })),
      }]}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => go("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <PortalTrackingProvider portal="condominio">
      <div className="min-h-screen flex antialiased">
        {/* Desktop sidebar */}
        <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
          {sidebar}
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
            {sidebar}
          </SheetContent>
        </Sheet>

        <div className="flex-1 lg:pl-64 min-w-0 overflow-x-clip">
          {/* Desktop header */}
          <header className="hidden lg:flex sticky top-0 z-20 h-16 items-center gap-4 px-6 lg:px-8 bg-card border-b border-border-soft">
            {/* Título del menú activo, como en Cobranza e Inmobiliaria: las páginas no pintan su h1. */}
            {cargandoPortal ? (
              <div className="min-w-0 flex-1"><Skeleton className="h-6 w-44" /></div>
            ) : (
              <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-foreground tracking-tight">{currentSection}</h1>
            )}
            <div className="flex shrink-0 items-center gap-3">
              <CondominioSelector />
              {/* "Ver como", campana y Mi cuenta: la pieza estándar de los portales. */}
              <AccionesUsuarioCabecera verComo={canImpersonate ? <CondominioImpersonationSelector /> : undefined} />
            </div>
          </header>

          {/* Mobile header */}
          <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
            <div className="flex items-center justify-between px-3 h-14">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setMobileOpen(true)}
                  className="p-2 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <CondominioSelector className="w-[150px]" />
                <AccionesUsuarioCabecera compacto />
              </div>
            </div>
            {/* En móvil el "Ver como" va en su propia fila, a todo lo ancho (como Inmobiliaria). */}
            {canImpersonate && (
              <FilaVerComoMovil>
                <CondominioImpersonationSelector />
              </FilaVerComoMovil>
            )}
          </header>
          <main className="px-4 py-5 sm:px-8 bg-background min-h-[calc(100vh-64px)]">
            {/* Pestañas y tarjetas KPI uniformes en todo el portal (mismo criterio que Cobranza). */}
            <TabBarPlano>
              <KpiCardUniforme>
                {cargandoPortal ? (
                  <EsqueletoPagina />
                ) : sinAcceso ? (
                  <EmptyState
                    titulo="No tienes condominios asignados"
                    texto="Pide al administrador que te dé acceso al proyecto del condominio que administras."
                  />
                ) : (
                  <Outlet />
                )}
              </KpiCardUniforme>
            </TabBarPlano>
          </main>
        </div>
      </div>
    </PortalTrackingProvider>
  );
};

export const PortalCondominioLayout = () => (
  <CondominioImpersonationProvider>
    <CondominioProvider>
      <PortalCondominioLayoutInner />
    </CondominioProvider>
  </CondominioImpersonationProvider>
);

export default PortalCondominioLayout;
